import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, createObjectWithCondition, getPostfixWithRobotId, getResponseError, handleErrorRTKAndAxiosAsync, swapElementsInArray } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { DATA_ELEMENT } from "@/constants/defaultValues";
import {
	ActionVariantType,
	AtomicCondition,
	AtomicConditionType,
	CategoryCondition,
	ConditionType,
	ConditionVariantType,
	DataElemActionEventType,
	DataElemRType,
	DataElemSubType,
	IActionInternalAsk,
	IAddingDataElemAction,
	IConditionConfirmation,
	IConditionData,
	IConditionInterval,
	IConditionVariable,
	IElement,
	IElementResponse,
	IEndpointResponse,
	IEntryConditions,
	IEntryInactivity,
	IEntryManual,
	IGetDataElemProps,
	IGetEndpointProps,
	IMulticondition,
	ISesItemConfigState,
	YesNoType,
} from "@/types/ses/sesItemConfigTypes";
import { addActiveEndpoint } from "./sesEndpointSlice";
import { addActiveDataElem } from "./sesDataElemSlice";
import { ExceptionType, RobotActionEventType } from "@/types/ses/sesRobotTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";
import { DataElemType } from "@/types/cloudTypes";
import { IAction, ICategoryAction, IDataElemSlaveData, IEndpointEntry, IEndpointRun, IEndpointRunInEntry } from "@/components/ConditionBlock/ConditionBlock.props";

type TNameOfTargetObject = "dataElement" | "endpoint";
const getTargetObject = (state: ISesItemConfigState, targetName: TNameOfTargetObject) => {
	const targetObject = targetName === "dataElement" ? state.dataElem.element : state.endpointElem.endpoint;
	return targetObject;
};

const initialState: ISesItemConfigState = {
	dataElem: {
		element: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	endpointElem: {
		endpoint: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// ЭД
export const getDataElem = createAsyncThunk("sesItemConfig/dataElem", async ({ robotId, elementId }: IGetDataElemProps, { getState, rejectWithValue, dispatch }) => {
	try {
		dispatch(addActiveDataElem(elementId));
		const response: AxiosResponse<IElementResponse | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.DATA_GET}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${elementId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// КТ
export const getEndpoint = createAsyncThunk("sesItemConfig/endpoint", async ({ robotId, endpointId }: IGetEndpointProps, { getState, rejectWithValue, dispatch }) => {
	try {
		dispatch(addActiveEndpoint(endpointId));
		const response: AxiosResponse<IEndpointResponse | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.ENDPOINT_GET}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${endpointId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesItemConfigSlice = createSlice({
	name: "sesItemConfig",
	initialState,
	reducers: {
		// добавление данных формы добавления ЭД
		addingAddElementFormData: (state, action: PayloadAction<{ name: string; type: DataElemType }>) => {
			state.dataElem.element = { ...DATA_ELEMENT, name: action.payload.name, type: action.payload.type, getOnlyAsNearest: action.payload.type === "see" ? true : false };
		},
		// изменение id ЭД
		changeDataElemId: (state, action: PayloadAction<string>) => {
			if (state.dataElem.element) {
				state.dataElem.element.id = action.payload;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных для режима долгого ожидания (dataWaiting)
		addDataWaiting: (state, action: PayloadAction<string>) => {
			if (state.dataElem.element) state.dataElem.element.dataWaiting.push(action.payload);
		},
		// изменение данных для режима долгого ожидания (dataWaiting)
		editDataWaiting: (state, action: PayloadAction<{ dataWaiting: string; idx: number }>) => {
			if (state.dataElem.element) {
				state.dataElem.element.dataWaiting.splice(action.payload.idx, 1, action.payload.dataWaiting);
			}
		},
		// удаление данных для режима долгого ожидания (dataWaiting)
		deleteDataWaiting: (state, action: PayloadAction<number>) => {
			if (state.dataElem.element) {
				state.dataElem.element.dataWaiting.splice(action.payload, 1);
			}
		},
		// -------------------------------------------------------------------------
		// добавление поля в настройку словаря значений
		addFieldToMatchesSetting: (state, action: PayloadAction<{ field: string }>) => {
			if (state.dataElem.element) state.dataElem.element.matches[action.payload.field] = "";
		},
		// изменение названия поля настройки словаря значений
		changeFieldNameInMatchesSettings: (state, action: PayloadAction<{ oldName: string; newName: string }>) => {
			if (state.dataElem.element) {
				state.dataElem.element.matches[action.payload.newName] = state.dataElem.element.matches[action.payload.oldName];
				delete state.dataElem.element.matches[action.payload.oldName];
			}
		},
		// изменение значения настройки словаря значений
		changeMatchesSettingValue: (state, action: PayloadAction<{ field: string; value: string }>) => {
			if (state.dataElem.element) state.dataElem.element.matches[action.payload.field] = action.payload.value;
		},
		// удаление поля из настройки словаря значений
		deleteFieldToMatchesSetting: (state, action: PayloadAction<{ field: string }>) => {
			if (state.dataElem.element) {
				delete state.dataElem.element.matches[action.payload.field];
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия сервисных данных ЭД
		addConditionSlaveDataDataElem: (state, action: PayloadAction<{ conditionBlockIdx: number; data: ConditionVariantType; orBlockIndex?: number }>) => {
			const element = state.dataElem.element;
			if (!element) return;

			const { conditionBlockIdx, data, orBlockIndex } = action.payload;
			const conditionBlock = element.slaveData[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data as any);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			element.slaveData.push([data]);
		},
		// изменение типа условия сервисных данных ЭД
		changeTypeConditionSlaveDataDataElem: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const element = state.dataElem.element;
			if (!element) return;

			const { conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const conditionBlock = element.slaveData[conditionBlockIdx];
			if (!conditionBlock) return;

			const condition = conditionBlock[conditionIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				conditionBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся — заменяем на одиночное условие
					if (condition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as AtomicCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as AtomicCondition);
			}
		},
		// изменение значений условия сервисных данных ЭД
		changeValuesConditionSlaveDataDataElem: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				data: ConditionVariantType;
				orBlockIndex?: number;
			}>
		) => {
			const element = state.dataElem.element;
			if (!element) return;

			const { conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const conditionBlock = element.slaveData[conditionBlockIdx];

			if (!conditionBlock) return;

			const condition = conditionBlock[conditionIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в одну ветку — разворачиваем в AND
					if (condition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...(condition.value as AtomicCondition[]));
					}
				}
				return;
			}

			// Обычный AND
			conditionBlock.splice(conditionIdx, 1, data as AtomicCondition);
		},
		reorderSlaveDataBlocks: (
			state,
			action: PayloadAction<{
				fromIdx: number;
				toIdx: number;
			}>
		) => {
			if (state.dataElem.element) {
				const blocks = state.dataElem.element.slaveData;
				const [movedBlock] = blocks.splice(action.payload.fromIdx, 1);
				blocks.splice(action.payload.toIdx, 0, movedBlock);
			}
		},
		reorderSlaveDataElement: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				fromIdx: number;
				toIdx: number;
			}>
		) => {
			if (state.dataElem.element) {
				const block = state.dataElem.element.slaveData[action.payload.conditionBlockIdx];
				const [movedItem] = block.splice(action.payload.fromIdx, 1);
				block.splice(action.payload.toIdx, 0, movedItem);
			}
		},
		reorderSlaveDataOrCondition: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex: number;
				fromIdx: number;
				toIdx: number;
			}>
		) => {
			const { conditionBlockIdx, conditionIdx, fromIdx, toIdx } = action.payload;
			if (!state.dataElem.element) return;
			const dataElemOrCondition = state.dataElem.element.slaveData[conditionBlockIdx][conditionIdx] as IMulticondition;
			if (dataElemOrCondition) {
				const swapedArr = swapElementsInArray<any>(dataElemOrCondition.value, fromIdx, toIdx);
				dataElemOrCondition.value = swapedArr;
			}
			// const entry = state.endpointElem.endpoint?.entry[conditionEntryBlockIdx];
			// if (!entry) return;

			// const block = entry.run[conditionBlockIdx];
			// const orCondition = block[orBlockIndex];

			// if (orCondition?.type !== "or") return;

			// const [moved] = orCondition.value.splice(fromIdx, 1);
			// orCondition.value.splice(toIdx, 0, moved);
		},
		// удаление условия сервисных данных ЭД
		deleteConditionSlaveDataDataElem: (state, action: PayloadAction<{ conditionBlockIdx: number; conditionIdx: number; orBlockIndex?: number }>) => {
			const element = state.dataElem.element;
			if (!element) return;

			const { conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;

			const conditionBlock = element.slaveData[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...(orCondition.value as AtomicCondition[]));
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				// если остался один элемент — удаляем весь блок
				element.slaveData.splice(conditionBlockIdx, 1);
			}
		},
		// -------------------------------------------------------------------------
		// изменение подтипа ЭД
		editDataElemRType: (state, action: PayloadAction<DataElemRType>) => {
			if (state.dataElem.element) state.dataElem.element.rtype = action.payload;
		},
		// изменение подтипа smc ЭД
		editDataElemSubType: (state, action: PayloadAction<DataElemSubType>) => {
			if (state.dataElem.element) state.dataElem.element.subtype = action.payload;
		},
		// изменение булевых значений ЭД
		changeBooleanValuesDataElem: (
			state,
			action: PayloadAction<{
				field: keyof Pick<IElement, "getOnlyAsNearest" | "injectToMessage" | "longWaiting" | "removeFromMessage" /* | "useOnceOnly" */>;
				value: boolean;
			}>
		) => {
			if (state.dataElem.element) {
				state.dataElem.element[action.payload.field] = action.payload.value;
			}
		},
		// добавление действия ЭД
		addingDataElemAction: (state, action: PayloadAction<IAddingDataElemAction>) => {
			if (state.dataElem.element) {
				// если есть событие действия
				if (action.payload.actionEvent in state.dataElem.element.actions) {
					// и если есть канал
					if (action.payload.channel in state.dataElem.element.actions[action.payload.actionEvent]) {
						state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel].push(action.payload.action); // добавляем действие
					}
					// иначе если нет канала - создаем
					else {
						state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel] = [action.payload.action];
					}
				}
				// иначе - создаем событие и канал
				else {
					state.dataElem.element.actions[action.payload.actionEvent] = {
						[action.payload.channel]: [action.payload.action],
					};
				}
			}
		},
		// замена действия ЭД
		replaceActionDataElem: (state, actionRedux: PayloadAction<{ actionEvent: DataElemActionEventType; channel: string; idx: number; action: ActionVariantType }>) => {
			if (state.dataElem.element) {
				const { actionEvent, channel, idx, action } = actionRedux.payload;
				state.dataElem.element.actions[actionEvent][channel][idx] = action;
			}
		},
		// изменение данных события действия ЭД (experiment)
		changeDataElemEventActionData: (
			state,
			actionRedux: PayloadAction<{ actionEvent: DataElemActionEventType; channel: string; idx: number; action: Partial<ActionVariantType> }>
		) => {
			if (state.dataElem.element) {
				const { actionEvent, channel, idx, action } = actionRedux.payload;
				state.dataElem.element.actions[actionEvent][channel][idx] = {
					...state.dataElem.element.actions[actionEvent][channel][idx],
					...(action as ActionVariantType),
				};
			}
		},
		// удаление действия ЭД
		deleteActionDataElem: (state, action: PayloadAction<{ actionEvent: DataElemActionEventType; channel: string; idx: number }>) => {
			state.dataElem.element?.actions[action.payload.actionEvent][action.payload.channel].splice(action.payload.idx, 1);
		},
		// изменение последовательности действий ЭД
		replaceDataElemActionList: (state, action: PayloadAction<{ actionEvent: DataElemActionEventType; channel: string; actionList: ActionVariantType[] }>) => {
			if (state.dataElem.element) {
				state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel] = action.payload.actionList;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных категории ЭД
		addCategoryDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const dataElem = state.dataElem.element;
			if (!dataElem) return;

			const actionBlock = dataElem.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			// Инициализация categories
			if (!actionBlock.categories) {
				actionBlock.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionBlock.categories)) {
				actionBlock.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionBlock.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},
		// изменение типа данных категории ЭД
		changeTypeCategoryDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.dataElem.element) return;

			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const actionItem = state.dataElem.element.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// схлопываем OR, если остался один блок
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},
		// изменение данных категории ЭД
		changeCategoryDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const element = state.dataElem.element;
			if (!element) return;

			const actionBlock = element.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			const categoryBlocks = actionBlock.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},
		// удаление данных категории ЭД
		deleteCategoryDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;
			const element = state.dataElem.element;
			if (!element) return;

			const actionBlock = element.actions[actionEvent]?.[channel]?.[channelIdx] as IActionInternalAsk;
			if (!actionBlock?.categories) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						categoryBlock.splice(
							categoryIdx,
							1,
							...(orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[])
						);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок — удаляем весь блок
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// если остался только один блок и один элемент — удаляем категорию целиком
					delete actionBlock.categories[categoryId];
					actionBlock.longContext = actionBlock.longContext?.filter((id) => id !== categoryId);
				}
			}
		},
		// изменение флага запроса в QAS с учетом прошлого контекста в ЭД
		changeLongContextDataElem: (
			state,
			action: PayloadAction<{ actionEvent: DataElemActionEventType; channel: string; channelIdx: number; categoryId: string; flag: boolean }>
		) => {
			if (state.dataElem.element) {
				const actionInternalAsk = state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk;
				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk).longContext = (
						state.dataElem.element.actions[action.payload.actionEvent][action.payload.channel][action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия запуска действия ЭД
		addConditionRunActionDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, data, orBlockIndex } = action.payload;

			const dataElem = state.dataElem.element;
			if (!dataElem) return;

			const actionBlock = dataElem.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock) return;

			// если условий ещё нет — создаём первый AND-блок
			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			actionBlock.conditions.push([data]);
		},
		// изменение типа условия запуска действия ЭД
		changeTypeConditionRunActionDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const dataElem = state.dataElem.element;
			if (!dataElem) return;

			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const conditionsBlock = dataElem.actions[actionEvent][channel][channelIdx].conditions?.[conditionBlockIdx];
			if (!conditionsBlock) return;

			const condition = conditionsBlock[conditionIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				conditionsBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data", confirmationValue)],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если в OR остался один блок — разворачиваем его
					if (condition.value.length === 1) {
						conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
					}
				}
				return;
			}

			if (condition.type === "or") {
				// ничего не делаем — поведение как в endpoint-версии
			} else {
				// обычная замена
				conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
			}
		},
		// изменение значений условия запуска действия ЭД
		changeValuesConditionRunActionDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;
			const element = state.dataElem.element;
			if (!element) return;

			const actionBlock = element.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}
				return;
			}

			// Обычный AND
			conditionBlock.splice(conditionIdx, 1, data);
		},
		// удаление условия запуска действия ЭД
		deleteConditionRunActionDataElem: (
			state,
			action: PayloadAction<{
				actionEvent: DataElemActionEventType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { actionEvent, channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;
			const element = state.dataElem.element;
			if (!element) return;

			const actionBlock = element.actions[actionEvent]?.[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];
				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},
		// добавление данных формы добавления КТ
		addingAddEndpointFormData: (state, action: PayloadAction<string>) => {
			state.endpointElem.endpoint = {
				id: "",
				name: action.payload,
				entryType: "conditions",
				entry: [],
				run: [],
				actions: {},
				type: "report",
				exceptions: {},
			};
		},
		// изменение id КТ
		changeEndpointId: (state, action: PayloadAction<string>) => {
			if (state.endpointElem.endpoint) {
				state.endpointElem.endpoint.id = action.payload;
			}
		},
		// добавление действия КТ
		addingEndpointAction: (state, action: PayloadAction<{ channel: string; action: ActionVariantType }>) => {
			if (state.endpointElem.endpoint) {
				// если есть канал
				if (action.payload.channel in state.endpointElem.endpoint.actions) {
					state.endpointElem.endpoint.actions[action.payload.channel].push(action.payload.action); // добавляем действие
				}
				// если нет канала - создаем
				else {
					state.endpointElem.endpoint.actions[action.payload.channel] = [action.payload.action];
				}
			}
		},
		// добавление действия входа КТ
		addingEndpointEntryAction: (state, action: PayloadAction<{ channel: string; action: ActionVariantType; withTimer: boolean }>) => {
			if (state.endpointElem.endpoint) {
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === action.payload.channel);
				// если есть канал
				if (indexInactivityItem >= 0) {
					entryInactivityList[indexInactivityItem].actions.push(action.payload.action); // добавляем действие
				} else {
					// если нет канала - создаем
					if (action.payload.withTimer) {
						(entryInactivityList as IEntryInactivity[]).push({
							channel: action.payload.channel,
							timer: 30,
							actions: [action.payload.action],
						});
					} else {
						(entryInactivityList as IEntryManual[]).push({
							channel: action.payload.channel,
							actions: [action.payload.action],
						});
					}
				}
			}
		},
		// изменение последовательности действий КТ
		replaceEndpointActionList: (state, action: PayloadAction<{ channel: string; actionList: ActionVariantType[] }>) => {
			if (state.endpointElem.endpoint) {
				state.endpointElem.endpoint.actions[action.payload.channel] = action.payload.actionList;
			}
		},
		// изменение последовательности действий входа КТ
		replaceEndpointEntryActionList: (state, action: PayloadAction<{ channel: string; actionList: ActionVariantType[] }>) => {
			if (state.endpointElem.endpoint) {
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === action.payload.channel);
				entryInactivityList[indexInactivityItem].actions = action.payload.actionList;
			}
		},
		// изменение таймера неактивности входа КТ
		changeTimerInactivity: (state, action: PayloadAction<{ channel: string; timer: number }>) => {
			if (state.endpointElem.endpoint) {
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === action.payload.channel);
				// если есть таймер
				if (indexInactivityItem >= 0) {
					entryInactivityList[indexInactivityItem].timer = action.payload.timer;
				}
				// иначе создаем
				else {
					entryInactivityList.push({
						channel: action.payload.channel,
						timer: action.payload.timer,
						actions: [],
					});
				}
			}
		},
		// удаление действия КТ
		deleteActionEndpoint: (state, action: PayloadAction<{ channel: string; idx: number }>) => {
			state.endpointElem.endpoint?.actions[action.payload.channel].splice(action.payload.idx, 1);
		},
		// удаление действия входа КТ
		deleteActionEndpointEntry: (state, action: PayloadAction<{ channel: string; idx: number }>) => {
			if (state.endpointElem.endpoint) {
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === action.payload.channel);
				entryInactivityList[indexInactivityItem]?.actions.splice(action.payload.idx, 1);
			}
		},
		// изменение данных действия  КТ  (experiment)
		changeEndpointActionData: (state, actionRedux: PayloadAction<{ channel: string; idx: number; action: Partial<ActionVariantType> }>) => {
			if (state.endpointElem.endpoint) {
				const { channel, idx, action } = actionRedux.payload;
				state.endpointElem.endpoint.actions[channel][idx] = {
					...state.endpointElem.endpoint.actions[channel][idx],
					...(action as ActionVariantType),
				};
			}
		},
		// замена действия КТ
		replaceActionEndpoint: (state, actionRedux: PayloadAction<{ channel: string; idx: number; action: ActionVariantType }>) => {
			if (state.endpointElem.endpoint) {
				const { channel, idx, action } = actionRedux.payload;
				state.endpointElem.endpoint.actions[channel][idx] = action;
			}
		},
		// изменение данных действия входа КТ (experiment)
		changeEndpointEntryActionData: (state, actionRedux: PayloadAction<{ channel: string; idx: number; action: Partial<ActionVariantType> }>) => {
			if (state.endpointElem.endpoint) {
				const { channel, idx, action } = actionRedux.payload;
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === channel);
				if (indexInactivityItem >= 0 && entryInactivityList[indexInactivityItem]) {
					entryInactivityList[indexInactivityItem].actions[idx] = {
						...entryInactivityList[indexInactivityItem]?.actions[idx],
						...(action as ActionVariantType),
					};
				}
			}
		},
		// замена действия входа КТ
		replaceActionEndpointEntry: (state, actionRedux: PayloadAction<{ channel: string; idx: number; action: ActionVariantType }>) => {
			if (state.endpointElem.endpoint) {
				const { channel, idx, action } = actionRedux.payload;
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === channel);
				entryInactivityList[indexInactivityItem].actions[idx] = action;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных категории КТ
		addCategoryDataEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const actionInternal = endpoint.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionInternal) return;

			if (!actionInternal.categories) {
				actionInternal.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionInternal.categories)) {
				actionInternal.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionInternal.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},

		// добавление данных категории входа КТ
		addCategoryDataEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			// entry: inactivity | manual
			const entryList = endpoint.entry as IEntryInactivity[] | IEntryManual[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionInternal = entryList[entryIdx].actions[channelIdx] as IActionInternalAsk | undefined;
			if (!actionInternal) return;

			// Инициализация categories
			if (!actionInternal.categories) {
				actionInternal.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionInternal.categories)) {
				actionInternal.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionInternal.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},
		// изменение типа данных категории КТ
		changeTypeCategoryEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.endpointElem.endpoint) return;

			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const actionItem = state.endpointElem.endpoint.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся — заменяем на одиночное условие
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}

				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},
		// изменение типа данных категории входа КТ
		changeTypeCategoryEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.endpointElem.endpoint) return;

			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const entryList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];

			const entryIndex = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIndex === -1) return;

			const entryItem = entryList[entryIndex];
			const actionItem = entryItem.actions?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},
		// изменение данных категории КТ
		changeCategoryDataEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const categoryBlocks = endpoint.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks.categories?.[categoryId]?.[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					// заменяем конкретный блок внутри OR
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — разворачиваем в AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},
		// изменение данных категории входа КТ
		changeCategoryDataEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IConditionConfirmation | IConditionVariable | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const entryList = endpoint.entry as IEntryInactivity[] | IEntryManual[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			const categoryBlocks = actionBlock.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},
		// удаление данных категории КТ
		deleteCategoryDataEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const actionBlock = endpoint.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock?.categories?.[categoryId]) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						const singleBranch = orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[];
						categoryBlock.splice(categoryIdx, 1, ...singleBranch);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок категорий
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// удаляем категорию полностью
					delete actionBlock.categories[categoryId];

					// обновляем longContext, если есть
					if (actionBlock.longContext) {
						actionBlock.longContext = actionBlock.longContext.filter((id) => id !== categoryId);
					}
				}
			}
		},
		// удаление данных категории входа КТ
		deleteCategoryDataEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			// entry: inactivity | manual
			const entryList = endpoint.entry as IEntryInactivity[] | IEntryManual[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx] as IActionInternalAsk;
			if (!actionBlock?.categories) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						categoryBlock.splice(
							categoryIdx,
							1,
							...(orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[])
						);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок — удаляем весь блок
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// если остался только один блок и один элемент — удаляем категорию целиком
					delete actionBlock.categories[categoryId];
					actionBlock.longContext = actionBlock.longContext?.filter((id) => id !== categoryId);
				}
			}
		},
		// изменение флага запроса в QAS с учетом прошлого контекста в КТ
		changeLongContextEndpoint: (state, action: PayloadAction<{ channel: string; channelIdx: number; categoryId: string; flag: boolean }>) => {
			if (state.endpointElem.endpoint) {
				const actionInternalAsk = state.endpointElem.endpoint.actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk;
				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(state.endpointElem.endpoint.actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk).longContext = (
						state.endpointElem.endpoint.actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// изменение флага запроса в QAS с учетом прошлого контекста во входе КТ
		changeLongContextEndpointEntry: (state, action: PayloadAction<{ channel: string; channelIdx: number; categoryId: string; flag: boolean }>) => {
			if (state.endpointElem.endpoint) {
				const entryInactivityList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
				const indexInactivityItem = entryInactivityList.findIndex((inactivityItem) => inactivityItem.channel === action.payload.channel);
				const actionInternalAsk = entryInactivityList[indexInactivityItem].actions[action.payload.channelIdx] as IActionInternalAsk;
				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(entryInactivityList[indexInactivityItem].actions[action.payload.channelIdx] as IActionInternalAsk).longContext = (
						entryInactivityList[indexInactivityItem].actions[action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия запуска действия КТ
		addConditionRunActionEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, data, orBlockIndex } = action.payload;
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const actionBlock = endpoint.actions[channel]?.[channelIdx];
			if (!actionBlock) return;

			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			actionBlock.conditions.push([data]);
		},
		// добавление условия запуска действия входа КТ
		addConditionRunActionEndpointEntry: (
			state,
			action: PayloadAction<{ channel: string; channelIdx: number; conditionBlockIdx: number; data: AtomicCondition; orBlockIndex?: number }>
		) => {
			const { channel, channelIdx, conditionBlockIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			// entry: inactivity | manual
			const entryList = endpoint.entry as IEntryInactivity[] | IEntryManual[];

			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx];
			if (!actionBlock) return;

			// Если условий ещё нет — создаём
			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}
			// существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// новый блок условий
			actionBlock.conditions.push([data]);
		},
		// изменение типа условия запуска действия КТ
		changeTypeConditionRunActionEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.endpointElem.endpoint) return;

			const { channel, channelIdx, conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const conditionsBlock = state.endpointElem.endpoint.actions[channel][channelIdx].conditions?.[conditionBlockIdx];
			if (!conditionsBlock) return;
			const condition = conditionsBlock[conditionIdx];
			if (!condition) return;

			if (conditionType === "or" && condition.type !== "or") {
				conditionsBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}
			// если редактируем внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType, confirmationValue));

					if (condition.value.length === 1) {
						conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType, confirmationValue));
					}
				}
				return;
			}
			if (condition.type === "or") {
				//
			} else {
				// обычная замена

				conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
			}
		},
		// изменение типа условия запуска действия входа КТ
		changeTypeConditionRunActionEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.endpointElem.endpoint) return;

			const { channel, channelIdx, conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;

			const entryList = endpoint.entry as (IEntryInactivity | IEntryManual)[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionsBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionsBlock) return;

			const targetCondition = conditionsBlock[conditionIdx];

			if (conditionType === "or") {
				conditionsBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [targetCondition as AtomicCondition, createObjectWithCondition("data")],
				});
				return;
			}
			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && targetCondition?.type === "or") {
				targetCondition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

				// если OR схлопывается до одного блока — превращаем в обычное условие
				if (targetCondition.value.length === 1) {
					conditionsBlock.splice(conditionIdx, 1, ...targetCondition.value);
				}

				return;
			}

			// обычная замена AND
			conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType, confirmationValue));
		},
		// изменение значений условия запуска действия КТ
		changeValuesConditionRunActionEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const { channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const conditionsBlock = endpoint.actions[channel]?.[channelIdx]?.conditions?.[conditionBlockIdx];

			if (!conditionsBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionsBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в один набор — разворачиваем в AND
					if (orCondition.value.length === 1) {
						conditionsBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}

				return;
			}

			// Обычный AND
			conditionsBlock.splice(conditionIdx, 1, data);
		},
		// изменение значений условия запуска действия входа КТ
		changeValuesConditionRunActionEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;
			if (!state.endpointElem.endpoint) return;

			const entryList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// схлопываем OR → AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}
				return;
			}

			// Обычный AND
			conditionBlock.splice(conditionIdx, 1, data);
		},
		// удаление условия запуска действия КТ
		deleteConditionRunActionEndpoint: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const actionBlock = endpoint.actions[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];
				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				// если остался один элемент — удаляем весь блок
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},
		// удаление условия запуска действия входа КТ
		deleteConditionRunActionEndpointEntry: (
			state,
			action: PayloadAction<{
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;
			if (!state.endpointElem.endpoint) return;

			const entryList = state.endpointElem.endpoint.entry as IEntryInactivity[] | IEntryManual[];
			const entryIdx = entryList.findIndex((entryItem) => entryItem.channel === channel);
			if (entryIdx === -1) return;

			const actionBlock = entryList[entryIdx].actions[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];

				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},
		// --------------------------------------------------------------
		// очистка входа КТ
		clearEntry: (state) => {
			if (state.endpointElem.endpoint) state.endpointElem.endpoint.entry = [];
		},
		// добавление условия ENTRY в КТ
		addConditionEntry: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				data: IConditionData | IConditionVariable;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionBlockIdx, data, orBlockIndex } = action.payload;
			if (!state.endpointElem.endpoint) return;

			const conditions = state.endpointElem.endpoint.entry as IEntryConditions[];

			// Существующий блок условий
			const conditionBlock = conditions[conditionBlockIdx]?.conditions;

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[orBlockIndex];
				if (orCondition?.type === "or") {
					(orCondition as IMulticondition).value.push(data);
					return;
				}
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			conditions.push({ conditions: [data], run: [] });
		},
		// добавление условия RUN в ENTRY в КТ
		addConditionRunInEntry: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionRunBlockIdx: number;
				data: IConditionData | IConditionVariable;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionEntryBlockIdx, conditionRunBlockIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const entry = endpoint.entry[conditionEntryBlockIdx];
			if (!entry) return;

			// Инициализация run, если его нет
			if (!(entry as any).run) {
				(entry as any).run = [[data]];
				return;
			}

			const conditionBlock = (entry as any).run[conditionRunBlockIdx];

			// OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// AND-блок (добавляем в существующий)
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий (новый AND)
			(entry as any).run.push([data]);
		},
		addConditionOrBlock: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				channel: string | null;
				conditionFor:
					| (IAction | ICategoryAction | IEndpointRun | IEndpointEntry | IEndpointRunInEntry | IDataElemSlaveData)["for"]
					| "dataException"
					| "endpointException";
				conditionType: "actions" | "run" | "entry" | "slaveData";
				actionId?: string;
				actionEvent: RobotActionEventType | ExceptionType | DataElemActionEventType | null;
			}>
		) => {
			const { conditionFor, actionId, conditionType, channel, conditionBlockIdx, actionEvent } = action.payload;
			const isAtomicCondition = (c: ConditionVariantType): c is AtomicCondition => c.type !== "or";
			const endpoint = state.endpointElem.endpoint;
			const dataElem = state.dataElem.element;

			if (endpoint && conditionType === "run" && conditionFor === "endpointException" && channel && actionEvent) {
				const exceptions = endpoint.exceptions;
				const exceptionsByActionEvent = exceptions[actionEvent];
				const desiredAction = exceptionsByActionEvent.actions[channel].find((el) => el.actionId === actionId);
				if (desiredAction && "conditions" in desiredAction && desiredAction.conditions) {
					const desiredCondition = desiredAction.conditions[conditionBlockIdx];
					const atomicConditions = desiredCondition.filter(isAtomicCondition);
					desiredAction.conditions[conditionBlockIdx] = [
						...atomicConditions,
						{
							type: "or",
							value: [createObjectWithCondition("data")],
						},
					];
				}

				return;
			}

			if (dataElem && conditionType === "entry" && conditionFor === "dataException" && channel && actionEvent) {
				const exceptions = dataElem.exceptions;
				const exceptionsByActionEvent = exceptions[actionEvent];
				const desiredAction = exceptionsByActionEvent.actions[channel].find((el) => el.actionId === actionId);
				if (desiredAction && "conditions" in desiredAction && desiredAction.conditions) {
					const desiredCondition = desiredAction.conditions[conditionBlockIdx];
					const atomicConditions = desiredCondition.filter(isAtomicCondition);
					desiredAction.conditions[conditionBlockIdx] = [
						...atomicConditions,
						{
							type: "or",
							value: [createObjectWithCondition("data")],
						},
					];
				}

				return;
			}

			if (dataElem && conditionType === "slaveData" && conditionFor === "dataElemSlaveData") {
				const slaveData = dataElem.slaveData;
				const desiredCondition = slaveData[conditionBlockIdx];
				const atomicConditions = desiredCondition.filter(isAtomicCondition);
				dataElem.slaveData[conditionBlockIdx] = [
					...atomicConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")],
					},
				];
				return;
			}

			if (endpoint && conditionFor === "action" && conditionType === "entry" && channel) {
				const endpointEntry = endpoint.entry as (IEntryManual | IEntryInactivity)[];

				if (endpointEntry) {
					const desiredActionsByChannel = endpointEntry.find((el) => el.channel === channel);
					if (desiredActionsByChannel) {
						const desiredAction = desiredActionsByChannel.actions.find((el) => el.actionId === actionId);
						if (desiredAction && "conditions" in desiredAction && desiredAction?.conditions) {
							const desiredCondition = desiredAction.conditions[conditionBlockIdx];
							const oldConditions = desiredAction.conditions[conditionBlockIdx];
							if (desiredCondition && oldConditions) {
								const atomicConditions = oldConditions.filter(isAtomicCondition);

								desiredAction.conditions[conditionBlockIdx] = [
									...atomicConditions,
									{
										type: "or",
										value: [createObjectWithCondition("data")],
									},
								];
							}
						}
					}
				}

				return;
			}

			if (endpoint && conditionFor === "endpointEntry" && conditionType === "run") {
				const desiredCondition = endpoint.entry[conditionBlockIdx] as IEntryConditions;

				// Берём только AtomicCondition и явно приводим
				const atomicOldConditions = desiredCondition.conditions.filter(isAtomicCondition);

				desiredCondition.conditions = [
					...atomicOldConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")] as AtomicCondition[],
					},
				];

				return;
			}

			if (endpoint && conditionFor === "endpointEntry" && conditionType === "entry") {
				const desiredCondition = endpoint.entry[conditionBlockIdx] as IEntryConditions;

				const atomicOldConditions = desiredCondition.conditions.filter(isAtomicCondition);

				desiredCondition.conditions = [
					...atomicOldConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")] as AtomicCondition[],
					},
				];

				return;
			}

			if (endpoint && conditionFor === "endpointRun" && conditionType === "run") {
				const oldConditions = endpoint.run[conditionBlockIdx];

				// фильтруем только атомарные условия и явно приводим к типу AtomicCondition[]
				const atomicConditions = oldConditions.filter(isAtomicCondition);

				endpoint.run[conditionBlockIdx] = [
					...atomicConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")] as AtomicCondition[],
					},
				];

				return;
			}

			if (dataElem && actionEvent && conditionFor === "action" && conditionType === "actions" && actionId && channel) {
				const desiredAction = dataElem.actions[actionEvent][channel].find((el) => el.actionId === actionId);
				const desiredConditions = desiredAction?.conditions ? desiredAction?.conditions[conditionBlockIdx] : null;
				if (!desiredConditions || !desiredAction) return;

				// убираем вложенные OR
				const atomicConditions = desiredConditions.filter(isAtomicCondition);

				desiredAction.conditions![conditionBlockIdx] = [
					...atomicConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")],
					},
				];

				return;
			}

			if (endpoint && conditionFor === "action" && conditionType === "actions" && actionId && channel) {
				const desiredAction = endpoint.actions[channel].find((el) => el.actionId === actionId);

				if (!desiredAction || !desiredAction.conditions) return;

				const idx = conditionBlockIdx;
				const oldCondition = desiredAction.conditions[idx];
				const atomicConditions = desiredAction.conditions[idx].filter(isAtomicCondition);

				if (!oldCondition) return;

				desiredAction.conditions[idx] = [
					...atomicConditions,
					{
						type: "or",
						value: [createObjectWithCondition("data")],
					},
				];
			}
		},
		// изменение типа данных условия ENTRY КТ
		changeTypeConditionEntryEndpoint: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: Extract<ConditionType, "data" | "variable" | "confirmation" | "or">;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const entryBlocks = endpoint.entry as IEntryConditions[];
			const conditionBlock = entryBlocks[conditionBlockIdx]?.conditions;
			if (!conditionBlock) return;

			const condition = conditionBlock[conditionIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				conditionBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся — заменяем на одиночное условие
					if (condition.value.length === 1) {
						conditionBlock.splice(
							conditionIdx,
							1,
							createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as IConditionData | IConditionVariable | IConditionConfirmation
						);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				conditionBlock.splice(
					conditionIdx,
					1,
					createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as IConditionData | IConditionVariable | IConditionConfirmation
				);
			}
		},
		// изменение типа данных условия RUN в ENTRY КТ
		changeTypeConditionRunInEntryEndpoint: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionRunBlockIdx: number;
				conditionIdx: number;
				conditionType: Extract<ConditionType, "data" | "confirmation" | "variable" | "or">;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionEntryBlockIdx, conditionRunBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const runBlocks = (endpoint.entry as IEntryConditions[])[conditionEntryBlockIdx].run;
			if (!runBlocks) return;

			const runBlock = runBlocks[conditionRunBlockIdx] as (IConditionData | IConditionVariable | IMulticondition)[]; // вот конкретный блок
			if (!runBlock) return;

			const condition = runBlock[conditionIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				runBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся в один набор — заменяем на одиночное условие
					if (condition.value.length === 1) {
						runBlock.splice(conditionIdx, 1, ...(condition.value as (IConditionData | IConditionVariable)[]));
					}
				}
				return;
			}

			// обычная замена (AND-блок)
			if (condition.type !== "or") {
				runBlock.splice(
					conditionIdx,
					1,
					createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as IConditionData | IConditionVariable | IMulticondition
				);
			}
		},
		// изменение значений условия ENTRY в КТ
		editConditionEntry: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const entryBlocks = endpoint.entry as IEntryConditions[];
			const conditionBlock = entryBlocks[conditionBlockIdx]?.conditions;
			if (!conditionBlock) return;

			const condition = conditionBlock[conditionIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...(condition.value as (IConditionData | IConditionVariable | IConditionConfirmation)[]));
					}
				}
				return;
			}

			// Обычный AND
			conditionBlock.splice(conditionIdx, 1, data);
		},
		// изменение значений условия RUN в ENTRY в КТ
		editConditionRunInEntry: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionRunBlockIdx: number;
				conditionIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IConditionInterval;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionEntryBlockIdx, conditionRunBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const runBlocks = (endpoint.entry as IEntryConditions[])[conditionEntryBlockIdx]?.run;
			if (!runBlocks) return;

			const runBlock = runBlocks[conditionRunBlockIdx] as (IConditionData | IConditionVariable | IConditionConfirmation | IConditionInterval | IMulticondition)[];
			if (!runBlock) return;

			const condition = runBlock[conditionIdx];

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as any);
				}
				return;
			}

			// Обычный AND
			runBlock.splice(conditionIdx, 1, data as any);
		},
		// удаление условия ENTRY в КТ
		deleteConditionEntry: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;

			if (!state.endpointElem.endpoint) return;

			const conditions = state.endpointElem.endpoint.entry as IEntryConditions[];
			const conditionBlock = conditions[conditionBlockIdx]?.conditions;
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...(orCondition.value as (IConditionData | IConditionVariable | IConditionConfirmation)[]));
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				// если остался один элемент — удаляем весь блок условий
				conditions.splice(conditionBlockIdx, 1);
			}
		},
		// удаление условия RUN в ENTRY в КТ
		deleteConditionRunInEntry: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionRunBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { conditionEntryBlockIdx, conditionRunBlockIdx, conditionIdx, orBlockIndex } = action.payload;

			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const entryBlocks = endpoint.entry as IEntryConditions[];
			const runBlocks = entryBlocks[conditionEntryBlockIdx]?.run;
			if (!runBlocks) return;

			const runBlock = runBlocks[conditionRunBlockIdx];
			if (!runBlock) return;
			const condition = runBlock[conditionIdx] as IConditionInterval | IConditionVariable | IConditionData | IConditionConfirmation | IMulticondition;
			// Удаление внутри OR
			if (typeof orBlockIndex === "number" && condition.type === "or") {
				condition.value.splice(orBlockIndex, 1);

				// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
				if (condition.value.length === 1) {
					const branch = condition.value[0];
					const branchArray = Array.isArray(branch) ? branch : [branch];
					runBlock.splice(conditionIdx, 1, ...(branchArray as any));
				}
				return;
			}

			// Удаление в обычном AND
			if (Array.isArray(runBlock) && runBlock.length > 1) {
				runBlock.splice(conditionIdx, 1);
			} else {
				// если остался один элемент или runBlock не массив — удаляем весь блок
				runBlocks.splice(conditionRunBlockIdx, 1);
			}
		},
		// изменение последовательности условий RUN в ENTRY КТ
		replaceEndpointRunInEntryList: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				runList: (IConditionData | IConditionVariable | IConditionConfirmation | IConditionInterval)[];
				conditionEntryIdx: number;
			}>
		) => {
			const { conditionEntryBlockIdx, runList, conditionEntryIdx } = action.payload;
			const endpoint = state.endpointElem.endpoint;
			if (endpoint) {
				(endpoint.entry as IEntryConditions[])[conditionEntryBlockIdx].run[conditionEntryIdx] = runList;
			}
		},
		replaceEndpointOrConditionsRunInEntryList: (
			state,
			action: PayloadAction<{
				conditionEntryBlockIdx: number;
				conditionIdx: number;
				conditionEntryIdx: number;
				runList: (IConditionData | IConditionVariable | IConditionConfirmation | IConditionInterval)[][];
			}>
		) => {
			const { conditionEntryBlockIdx, conditionEntryIdx, conditionIdx, runList } = action.payload;
			const endpoint = state.endpointElem.endpoint;
			if (endpoint) {
				const condition = (endpoint.entry as IEntryConditions[])[conditionEntryBlockIdx].run[conditionEntryIdx][conditionIdx];

				if (!("value" in condition)) return;
				(condition as any).value = runList;
			}
		},
		// --------------------------------------------------------------
		// добавление условия запуска КТ
		addConditionRun: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const { conditionBlockIdx, data, orBlockIndex } = action.payload;

			const conditions = endpoint.run;

			// если условий запуска ещё нет
			if (!conditions.length) {
				conditions.push([data as IConditionData | IConditionVariable | IMulticondition]);
				return;
			}

			const conditionBlock = conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data as IConditionData | IConditionVariable | IMulticondition);
				return;
			}

			// Новый блок условий
			conditions.push([data as IConditionData | IConditionVariable | IMulticondition]);
		},
		// изменение типа данных условия запуска КТ
		changeTypeConditionRunEndpoint: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			if (!state.endpointElem.endpoint) return;

			const { conditionBlockIdx, conditionIdx, conditionType, confirmationValue, orBlockIndex } = action.payload;

			const runBlocks = state.endpointElem.endpoint.run;
			const conditionBlock = runBlocks?.[conditionBlockIdx];
			if (!conditionBlock) return;

			const condition = conditionBlock[conditionIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				conditionBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся — разворачиваем в обычное условие
					if (condition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType, confirmationValue) as IConditionData | IConditionVariable);
					}
				}
				return;
			}

			// обычная замена (не OR)
			if (condition.type !== "or") {
				conditionBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as IConditionData | IConditionVariable);
			}
		},
		// изменение значений условия запуска КТ
		editConditionRun: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const { conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const conditionsBlock = endpoint.run[conditionBlockIdx];
			if (!conditionsBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionsBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// если OR схлопнулся в одну ветку — разворачиваем в AND
					if (orCondition.value.length === 1) {
						//ts-ignore
						conditionsBlock.splice(conditionIdx, 1, ...(orCondition.value as (IConditionVariable | IConditionData | IMulticondition)[]));
					}
				}

				return;
			}

			// Обычный AND
			conditionsBlock.splice(conditionIdx, 1, data as IConditionVariable | IConditionData | IMulticondition);
		},
		// удаление условия запуска КТ
		deleteConditionRun: (
			state,
			action: PayloadAction<{
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const endpoint = state.endpointElem.endpoint;
			if (!endpoint) return;

			const { conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;

			const conditions = endpoint.run;
			const conditionBlock = conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...(orCondition.value as (IConditionVariable | IConditionData | IMulticondition)[]));
					}
				}

				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				// если в блоке был один элемент — удаляем весь блок
				conditions.splice(conditionBlockIdx, 1);
			}
		},
		// ---------------------- ИСКЛЮЧЕНИЯ ----------------------
		// изменение max кол-ва исключений подряд
		changeMaxNumberException: (state, action: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; max: number }>) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (targetObject) {
				// если есть исключение
				if (action.payload.exception in targetObject.exceptions) {
					// если есть поле max - меняем значение
					if ("max" in targetObject.exceptions[action.payload.exception]) {
						targetObject.exceptions[action.payload.exception].max = action.payload.max;
					}
					// иначе создаем поле max
					else {
						targetObject.exceptions[action.payload.exception] = {
							...targetObject.exceptions[action.payload.exception],
							max: action.payload.max,
						};
					}
				}
				// иначе создаем исключение и поле max
				else {
					targetObject.exceptions = {
						...targetObject.exceptions,
						[action.payload.exception]: {
							max: action.payload.max,
							actions: {},
						},
					};
				}
			}
		},
		// добавление исключения
		addException: (state, action: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; channel: string; action: ActionVariantType }>) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (targetObject) {
				// если есть исключение
				if (action.payload.exception in targetObject.exceptions) {
					// и если есть канал
					if (targetObject.exceptions[action.payload.exception].actions && targetObject.exceptions[action.payload.exception].actions[action.payload.channel]) {
						targetObject.exceptions[action.payload.exception].actions[action.payload.channel].push(action.payload.action); // добавляем действие
					}
					// если нет канала - создаем
					else {
						targetObject.exceptions[action.payload.exception].actions = {
							...targetObject.exceptions[action.payload.exception].actions,
							[action.payload.channel]: [action.payload.action],
						};
					}
				}
				// если нет события действия - создаем событие, канал
				else {
					targetObject.exceptions = {
						...targetObject.exceptions,
						[action.payload.exception]: {
							actions: {
								[action.payload.channel]: [action.payload.action],
							},
							max: 0,
						},
					};
				}
			}
		},
		// изменение действия исключения
		replaceActionException: (
			state,
			actionRedux: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; channel: string; idx: number; action: ActionVariantType }>
		) => {
			const { targetObject, exception, channel, idx, action } = actionRedux.payload;
			const tObject = getTargetObject(state, targetObject);
			if (tObject) {
				tObject.exceptions[exception].actions[channel][idx] = action;
			}
		},
		// изменение данных действия исключения (experiment)
		changeExceptionActionData: (
			state,
			actionRedux: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				idx: number;
				action: Partial<ActionVariantType>;
			}>
		) => {
			const { targetObject, exception, channel, idx, action } = actionRedux.payload;
			const tObject = getTargetObject(state, targetObject);
			if (tObject) {
				tObject.exceptions[exception].actions[channel][idx] = {
					...tObject.exceptions[exception].actions[channel][idx],
					...(action as ActionVariantType),
				};
			}
		},
		// удаление исключения
		deleteException: (state, action: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; channel: string; idx: number }>) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			targetObject?.exceptions[action.payload.exception].actions[action.payload.channel].splice(action.payload.idx, 1);
		},
		// изменение последовательности исключений
		reorderExceptionList: (
			state,
			action: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; channel: string; actionList: ActionVariantType[] }>
		) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (targetObject) {
				targetObject.exceptions[action.payload.exception].actions[action.payload.channel] = action.payload.actionList;
			}
		},
		// -------------------------------------------------------------------------
		// добавление данных категории исключения
		addCategoryDataException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				data: IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { targetObject: targetName, exception, channel, channelIdx, categoryId, categoryBlockIdx, data, orBlockIndex } = action.payload;

			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionInternal = targetObject.exceptions[exception]?.actions[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionInternal) return;

			// Инициализация categories
			if (!actionInternal.categories) {
				actionInternal.categories = { [categoryId]: [[data as CategoryCondition]] };
				return;
			}

			if (!(categoryId in actionInternal.categories)) {
				actionInternal.categories[categoryId] = [[data as CategoryCondition]];
				return;
			}

			const categoryBlocks = actionInternal.categories[categoryId];

			// Добавление в существующий categoryBlock
			if (categoryBlockIdx < categoryBlocks.length) {
				const categoryBlock = categoryBlocks[categoryBlockIdx];

				// Добавление в OR-блок
				if (typeof orBlockIndex === "number" && categoryBlock[orBlockIndex]?.type === "or") {
					(categoryBlock[orBlockIndex] as IMulticondition).value.push(data as AtomicCondition);
					return;
				}

				// Добавление в существующий AND-блок
				categoryBlock.push(data as CategoryCondition);
				return;
			}

			// Новый categoryBlock
			categoryBlocks.push([data as CategoryCondition]);
		},

		// изменение типа данных категории исключения
		changeTypeCategoryException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const {
				targetObject: targetName,
				exception,
				channel,
				channelIdx,
				categoryId,
				categoryBlockIdx,
				categoryIdx,
				conditionType,
				confirmationValue,
				orBlockIndex,
			} = action.payload;

			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionItem = targetObject.exceptions?.[exception]?.actions?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionItem) return;

			const categoryBlocks = actionItem.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// превращаем обычное условие в OR
			if (conditionType === "or" && condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, {
					type: "or",
					value: [condition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && conditionType !== "or") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

					// если OR схлопнулся
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
					}
				}
				return;
			}

			// обычная замена
			if (condition.type !== "or") {
				categoryBlock.splice(categoryIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue) as CategoryCondition);
			}
		},

		// изменение данных категории исключения
		changeCategoryDataException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				data: IConditionData | IMulticondition;
				orBlockIndex?: number;
			}>
		) => {
			const { targetObject: targetName, exception, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, data, orBlockIndex } = action.payload;

			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception]?.actions?.[channel]?.[channelIdx] as IActionInternalAsk | undefined;
			if (!actionBlock) return;

			const categoryBlocks = actionBlock.categories?.[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			const condition = categoryBlock[categoryIdx];
			if (!condition) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				if (condition.type === "or") {
					condition.value.splice(orBlockIndex, 1, data as AtomicCondition);

					// если OR схлопнулся в один набор — превращаем в обычный AND
					if (condition.value.length === 1) {
						categoryBlock.splice(categoryIdx, 1, ...(condition.value as CategoryCondition[]));
					}
				}
				return;
			}

			// Обычная замена (AND)
			categoryBlock[categoryIdx] = data as CategoryCondition;
		},

		// удаление данных категории исключения
		deleteCategoryDataException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				categoryId: string;
				categoryBlockIdx: number;
				categoryIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { targetObject: targetName, exception, channel, channelIdx, categoryId, categoryBlockIdx, categoryIdx, orBlockIndex } = action.payload;

			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception].actions[channel][channelIdx] as IActionInternalAsk;
			if (!actionBlock?.categories) return;

			const categoryBlocks = actionBlock.categories[categoryId];
			if (!categoryBlocks) return;

			const categoryBlock = categoryBlocks[categoryBlockIdx];
			if (!categoryBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = categoryBlock[categoryIdx];
				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1);

					// если OR осталась одна ветка — разворачиваем в обычный AND
					if (orCondition.value.length === 1) {
						categoryBlock.splice(
							categoryIdx,
							1,
							...(orCondition.value[0] as unknown as (IConditionData | IConditionVariable | IConditionConfirmation | IMulticondition)[])
						);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (categoryBlock.length > 1) {
				categoryBlock.splice(categoryIdx, 1);
			} else {
				// если остался один блок — удаляем весь блок
				if (categoryBlocks.length > 1) {
					categoryBlocks.splice(categoryBlockIdx, 1);
				} else {
					// если осталась только одна категория — удаляем её полностью
					delete actionBlock.categories[categoryId];
					actionBlock.longContext = actionBlock.longContext?.filter((id) => id !== categoryId);
				}
			}
		},

		// изменение флага запроса в QAS с учетом прошлого контекста исключения
		changeLongContextException: (
			state,
			action: PayloadAction<{ targetObject: TNameOfTargetObject; exception: ExceptionType; channel: string; channelIdx: number; categoryId: string; flag: boolean }>
		) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (targetObject) {
				const actionInternalAsk = targetObject.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk;
				// если стоит галочка - добавляем в список, иначе убираем
				if (action.payload.flag) {
					if (Array.isArray(actionInternalAsk.longContext)) actionInternalAsk.longContext.push(action.payload.categoryId);
					else actionInternalAsk.longContext = [action.payload.categoryId];
				} else {
					(targetObject.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk).longContext = (
						targetObject.exceptions[action.payload.exception].actions[action.payload.channel][action.payload.channelIdx] as IActionInternalAsk
					).longContext?.filter((categoryId) => categoryId !== action.payload.categoryId);
				}
			}
		},
		// -------------------------------------------------------------------------
		// добавление условия запуска действия исключения
		addConditionRunActionException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { channel, channelIdx, conditionBlockIdx, data, orBlockIndex, exception } = action.payload;

			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception]?.actions?.[channel]?.[channelIdx];
			if (!actionBlock) return;

			// Если условий ещё нет — создаём
			if (!actionBlock.conditions) {
				actionBlock.conditions = [[data]];
				return;
			}

			const conditions = actionBlock.conditions;
			const conditionBlock = conditions[conditionBlockIdx];

			// Добавление в OR-блок
			if (conditionBlock && typeof orBlockIndex === "number" && conditionBlock[orBlockIndex]?.type === "or") {
				(conditionBlock[orBlockIndex] as IMulticondition).value.push(data);
				return;
			}

			// Добавление в существующий AND-блок
			if (conditionBlock) {
				conditionBlock.push(data);
				return;
			}

			// Новый блок условий
			conditions.push([data]);
		},
		// изменение типа условия запуска действия исключения
		changeTypeConditionRunActionException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				conditionType: ConditionType;
				confirmationValue?: YesNoType;
				orBlockIndex?: number;
			}>
		) => {
			const {
				targetObject: targetObjectName,
				exception,
				channel,
				channelIdx,
				conditionBlockIdx,
				conditionIdx,
				conditionType,
				confirmationValue,
				orBlockIndex,
			} = action.payload;

			const targetObject = getTargetObject(state, targetObjectName);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception]?.actions[channel]?.[channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionsBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionsBlock) return;

			const targetCondition = conditionsBlock[conditionIdx];
			if (!targetCondition) return;

			// превращение обычного условия в OR
			if (conditionType === "or" && targetCondition.type !== "or") {
				conditionsBlock.splice(conditionIdx, 1, {
					type: "or",
					value: [targetCondition as AtomicCondition, createObjectWithCondition("data")],
				});
				return;
			}

			// редактирование внутри OR
			if (typeof orBlockIndex === "number" && targetCondition.type === "or") {
				targetCondition.value.splice(orBlockIndex, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));

				// если OR схлопывается до одного блока — превращаем в обычное условие
				if (targetCondition.value.length === 1) {
					conditionsBlock.splice(conditionIdx, 1, ...targetCondition.value);
				}

				return;
			}

			// обычная замена AND

			conditionsBlock.splice(conditionIdx, 1, createObjectWithCondition(conditionType as AtomicConditionType, confirmationValue));
		},
		// изменение значений условия запуска действия исключения
		changeValuesConditionRunActionException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				data: AtomicCondition;
				orBlockIndex?: number;
			}>
		) => {
			const { targetObject: targetName, exception, channel, channelIdx, conditionBlockIdx, conditionIdx, data, orBlockIndex } = action.payload;

			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception]?.actions?.[channel]?.[channelIdx];
			if (!actionBlock) return;

			const conditionBlock = actionBlock.conditions?.[conditionBlockIdx];
			if (!conditionBlock) return;

			// Редактирование внутри OR
			if (typeof orBlockIndex === "number") {
				const orCondition = conditionBlock[conditionIdx];

				if (orCondition?.type === "or") {
					orCondition.value.splice(orBlockIndex, 1, data);

					// схлопываем OR → AND
					if (orCondition.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orCondition.value);
					}
				}

				return;
			}

			// Обычный AND
			conditionBlock.splice(conditionIdx, 1, data);
		},
		// удаление условия запуска действия исключения
		deleteConditionRunActionException: (
			state,
			action: PayloadAction<{
				targetObject: TNameOfTargetObject;
				exception: ExceptionType;
				channel: string;
				channelIdx: number;
				conditionBlockIdx: number;
				conditionIdx: number;
				orBlockIndex?: number;
			}>
		) => {
			const { targetObject: targetName, exception, channel, channelIdx, conditionBlockIdx, conditionIdx, orBlockIndex } = action.payload;
			const targetObject = getTargetObject(state, targetName);
			if (!targetObject) return;

			const actionBlock = targetObject.exceptions[exception].actions[channel][channelIdx];
			if (!actionBlock?.conditions) return;

			const conditionBlock = actionBlock.conditions[conditionBlockIdx];
			if (!conditionBlock) return;

			// Удаление внутри OR
			if (typeof orBlockIndex === "number") {
				const orConditions = conditionBlock[conditionIdx];

				if (orConditions?.type === "or") {
					orConditions.value.splice(orBlockIndex, 1);

					// если после удаления OR осталась одна ветка — разворачиваем в обычный AND
					if (orConditions.value.length === 1) {
						conditionBlock.splice(conditionIdx, 1, ...orConditions.value);
					}
				}
				return;
			}

			// Удаление в обычном AND
			if (conditionBlock.length > 1) {
				conditionBlock.splice(conditionIdx, 1);
			} else {
				actionBlock.conditions.splice(conditionBlockIdx, 1);
			}
		},
		// удаление всех исключений
		deleteExceptions: (state, action: PayloadAction<{ targetObject: TNameOfTargetObject }>) => {
			const targetObject = getTargetObject(state, action.payload.targetObject);
			if (targetObject) {
				targetObject.exceptions = {};
			}
		},
		// -------------------------------------------------------------------------
		// очистка state
		clearState: (state) => {
			state.dataElem = initialState.dataElem;
			state.endpointElem = initialState.endpointElem;
		},
		// очистка элемента данных
		clearDataElem: (state) => {
			state.dataElem = initialState.dataElem;
		},
		// очистка конечной точки
		clearEndpointElem: (state) => {
			state.endpointElem = initialState.endpointElem;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDataElem.pending, (state) => {
				state.dataElem.status = RequestStatus.LOADING;
			})
			.addCase(getDataElem.fulfilled, (state, action) => {
				if (action.payload && "data" in action.payload) {
					state.dataElem.status = RequestStatus.IDLE;
					state.dataElem.element = action.payload.data;
				} else state.dataElem.status = RequestStatus.FAILED;
			})
			.addCase(getDataElem.rejected, (state, action: PayloadAction<unknown>) => {
				state.dataElem.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.dataElem.error = error;
				state.dataElem.message = message;
			})
			.addCase(getEndpoint.pending, (state) => {
				state.endpointElem.status = RequestStatus.LOADING;
			})
			.addCase(getEndpoint.fulfilled, (state, action) => {
				if (action.payload && "data" in action.payload) {
					state.endpointElem.status = RequestStatus.IDLE;
					state.endpointElem.endpoint = action.payload.data;
				} else state.endpointElem.status = RequestStatus.FAILED;
			})
			.addCase(getEndpoint.rejected, (state, action: PayloadAction<unknown>) => {
				state.endpointElem.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.endpointElem.error = error;
				state.endpointElem.message = message;
			});
	},
});

export const {
	reorderSlaveDataOrCondition,
	replaceEndpointOrConditionsRunInEntryList,
	addConditionOrBlock,
	addingAddElementFormData,
	changeDataElemId,
	addDataWaiting,
	editDataWaiting,
	deleteDataWaiting,
	addFieldToMatchesSetting,
	changeFieldNameInMatchesSettings,
	changeMatchesSettingValue,
	deleteFieldToMatchesSetting,
	addConditionSlaveDataDataElem,
	changeTypeConditionSlaveDataDataElem,
	changeValuesConditionSlaveDataDataElem,
	deleteConditionSlaveDataDataElem,
	reorderSlaveDataBlocks,
	reorderSlaveDataElement,
	editDataElemRType,
	editDataElemSubType,
	changeBooleanValuesDataElem,
	addingDataElemAction,
	replaceActionDataElem,
	changeDataElemEventActionData,
	deleteActionDataElem,
	replaceDataElemActionList,
	addCategoryDataElem,
	changeTypeCategoryDataElem,
	changeCategoryDataElem,
	deleteCategoryDataElem,
	changeLongContextDataElem,
	addConditionRunActionDataElem,
	changeTypeConditionRunActionDataElem,
	changeValuesConditionRunActionDataElem,
	deleteConditionRunActionDataElem,
	addingAddEndpointFormData,
	changeEndpointId,
	addingEndpointAction,
	replaceEndpointActionList,
	replaceEndpointEntryActionList,
	addingEndpointEntryAction,
	changeTimerInactivity,
	deleteActionEndpoint,
	deleteActionEndpointEntry,
	changeEndpointActionData,
	replaceActionEndpoint,
	changeEndpointEntryActionData,
	replaceActionEndpointEntry,
	addCategoryDataEndpoint,
	addCategoryDataEndpointEntry,
	changeTypeCategoryEndpoint,
	changeTypeCategoryEndpointEntry,
	changeCategoryDataEndpoint,
	changeCategoryDataEndpointEntry,
	deleteCategoryDataEndpoint,
	deleteCategoryDataEndpointEntry,
	changeLongContextEndpoint,
	changeLongContextEndpointEntry,
	addConditionRunActionEndpoint,
	addConditionRunActionEndpointEntry,
	changeTypeConditionRunActionEndpoint,
	changeTypeConditionRunActionEndpointEntry,
	changeValuesConditionRunActionEndpoint,
	changeValuesConditionRunActionEndpointEntry,
	deleteConditionRunActionEndpoint,
	deleteConditionRunActionEndpointEntry,
	clearEntry,
	addConditionEntry,
	addConditionRunInEntry,
	changeTypeConditionEntryEndpoint,
	changeTypeConditionRunInEntryEndpoint,
	editConditionEntry,
	editConditionRunInEntry,
	deleteConditionEntry,
	deleteConditionRunInEntry,
	replaceEndpointRunInEntryList,
	addConditionRun,
	changeTypeConditionRunEndpoint,
	editConditionRun,
	deleteConditionRun,
	changeMaxNumberException,
	addException,
	replaceActionException,
	changeExceptionActionData,
	deleteException,
	reorderExceptionList,
	addCategoryDataException,
	changeTypeCategoryException,
	changeCategoryDataException,
	deleteCategoryDataException,
	changeLongContextException,
	addConditionRunActionException,
	changeTypeConditionRunActionException,
	changeValuesConditionRunActionException,
	deleteConditionRunActionException,
	deleteExceptions,
	clearState,
	clearDataElem,
	clearEndpointElem,
} = sesItemConfigSlice.actions;

export const selectDataElem = (state: RootState) => state.sesItemConfig.dataElem;
export const selectEndpointElem = (state: RootState) => state.sesItemConfig.endpointElem;

export default sesItemConfigSlice.reducer;
